import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Phone } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const STATUSES = ["All", "Pending", "Contacted", "Resolved"];

const STATUS_STYLES = {
  Pending: "bg-[#C27803]/10 text-[#9A5E02] border-[#C27803]/40",
  Contacted: "bg-[#4E5C4E]/10 text-[#4E5C4E] border-[#4E5C4E]/40",
  Resolved: "bg-[#8A7E70]/10 text-[#8A7E70] border-[#8A7E70]/40",
};

export default function EnquiriesTab({ onChanged }) {
  const [enquiries, setEnquiries] = useState([]);
  const [filter, setFilter] = useState("All");

  const load = () =>
    api
      .get("/enquiries", { params: { status: filter } })
      .then((r) => setEnquiries(r.data))
      .catch(() => {});

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const setStatus = async (enquiry, status) => {
    try {
      await api.patch(`/enquiries/${enquiry.id}/status`, { status });
      toast.success(`Marked as ${status}`);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-enquiries-tab">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-serif text-xl font-semibold text-[#292725]">{enquiries.length} enquiries</h2>
        <div className="flex gap-2" data-testid="enquiry-status-filters">
          {STATUSES.map((s) => (
            <button
              key={s}
              data-testid={`enquiry-filter-${s.toLowerCase()}`}
              onClick={() => setFilter(s)}
              className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                filter === s ? "border-[#8C251C] bg-[#8C251C] text-[#FDFBF7]" : "border-[#D8CDBC] bg-[#FDFBF7] text-[#292725] hover:border-[#C27803]"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {enquiries.length === 0 ? (
        <div className="mt-5 rounded-md border border-dashed border-[#D8CDBC] bg-[#FDFBF7] py-16 text-center" data-testid="enquiries-empty-state">
          <p className="font-serif text-xl text-[#292725]">The letterbox is empty</p>
          <p className="mt-1 text-sm text-[#8A7E70]">New enquiries from the contact page will appear here.</p>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          {enquiries.map((e) => (
            <div key={e.id} className="rounded-md border border-[#D8CDBC] bg-[#FDFBF7] p-5 shadow-sm" data-testid={`enquiry-card-${e.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-serif text-lg font-semibold text-[#292725]">{e.name}</p>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#8A7E70]">
                    <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {e.email}</span>
                    {e.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {e.phone}</span>}
                    <span>{new Date(e.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-sm border px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${STATUS_STYLES[e.status] || ""}`} data-testid={`enquiry-status-${e.id}`}>
                    {e.status}
                  </span>
                  <select
                    value={e.status}
                    onChange={(ev) => setStatus(e, ev.target.value)}
                    data-testid={`enquiry-status-select-${e.id}`}
                    className="rounded-md border border-[#D8CDBC] bg-[#FDFBF7] px-2 py-1.5 text-xs text-[#292725] focus:border-[#C27803] focus:outline-none"
                  >
                    {STATUSES.slice(1).map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="rounded-sm bg-[#F4EFE6] px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-[#8A7E70]">{e.category}</span>
                {e.product_name && (
                  <span className="rounded-sm bg-[#C27803]/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-[#9A5E02]">
                    re: {e.product_name}
                  </span>
                )}
              </div>
              <p className="mt-3 border-t border-dashed border-[#E8DFC8] pt-3 text-sm leading-relaxed text-[#5C554B]">{e.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
